import { type ApiClient, createApiClient, createFetchTransport, type Transport } from "./client";
import { createEventSourceRealtime, type RealtimeSource } from "./realtime";

/**
 * Selects the API runtime once at startup.
 *
 *   VITE_USE_MOCK_API=true  → in-browser mock backend (src/api/mock), demo data, simulated realtime
 *   otherwise               → the Rogue backend over HTTP + SSE
 *
 * The mock module is only imported (lazily) in mock mode, so it never ships to production.
 */
export const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === "true";
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

export interface ApiRuntime {
  api: ApiClient;
  realtime: RealtimeSource;
  mock: boolean;
  /** Demo controls, mock mode only. */
  mockControls?: { simulateSellerMessage: (conversationId?: string, body?: string) => string | null };
}

let runtime: ApiRuntime | undefined;

export async function initRuntime(): Promise<ApiRuntime> {
  if (runtime) return runtime;
  if (USE_MOCK_API) {
    const { createMockBackend } = await import("./mock");
    const mock = createMockBackend();
    runtime = {
      api: createApiClient(mock.transport),
      realtime: mock.realtime,
      mock: true,
      mockControls: { simulateSellerMessage: mock.simulateSellerMessage },
    };
  } else {
    runtime = {
      api: createApiClient(createFetchTransport(API_BASE_URL)),
      realtime: createEventSourceRealtime(API_BASE_URL),
      mock: false,
    };
  }
  return runtime;
}

/** Tests and the mock toolbar install an explicit runtime. */
export function setRuntime(next: { transport: Transport; realtime: RealtimeSource; mock: boolean; mockControls?: ApiRuntime["mockControls"] }): ApiRuntime {
  runtime = { api: createApiClient(next.transport), realtime: next.realtime, mock: next.mock, mockControls: next.mockControls };
  return runtime;
}

export function getRuntime(): ApiRuntime {
  if (!runtime) throw new Error("API runtime not initialized");
  return runtime;
}

/** The API client used by every query and mutation. */
export const api: ApiClient = {
  request: (req) => getRuntime().api.request(req),
  get: (path, opts) => getRuntime().api.get(path, opts),
  post: (path, body, opts) => getRuntime().api.post(path, body, opts),
  put: (path, body) => getRuntime().api.put(path, body),
  patch: (path, body) => getRuntime().api.patch(path, body),
  delete: (path) => getRuntime().api.delete(path),
  download: (path, opts) => getRuntime().api.download(path, opts),
};
